import { clearSession, getToken } from "./auth";

// Runtime config.js (Docker image) first, then REACT_APP_API_BASE_URL from
// app/.env for `npm start`.
const baseURL = window.__ENV__?.API_BASE_URL || process.env.REACT_APP_API_BASE_URL || "";

export class RequestError extends Error {
  status: number;
  body: unknown;

  constructor(message: string, status: number, body: unknown) {
    super(message);
    this.name = "RequestError";
    this.status = status;
    this.body = body;
  }
}

export async function request<T = unknown>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getToken();
  const response = await fetch(`${baseURL}${path}`, {
    ...options,
    headers: {
      ...(options.body ? { "Content-Type": "application/json" } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });
  const body = response.headers.get("content-type")?.includes("application/json")
    ? await response.json()
    : null;
  if (!response.ok) {
    if (response.status === 401) clearSession();
    // Server errors are reported (by lib/errorReporting) except for the event
    // upload itself, which would report its own failure forever.
    if (response.status >= 500 && !path.startsWith("/api/events")) {
      window.dispatchEvent(
        new CustomEvent("cpd:api-error", {
          detail: { status: response.status, path: path.split("?")[0], message: "Server error" },
        })
      );
    }
    const message =
      (body as { error?: string; message?: string } | null)?.error ||
      (body as { message?: string } | null)?.message ||
      "Request failed";
    throw new RequestError(message, response.status, body);
  }
  return body as T;
}

export async function revokeSession(token: string): Promise<void> {
  const response = await fetch(`${baseURL}/api/auth/logout`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) throw new Error("Could not revoke session");
}

export function apiURL(path: string): string {
  return `${baseURL}${path}`;
}

export function errorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : "Unexpected error";
}
